import {Helmet} from '@dr.pogodin/react-helmet';
import {Show, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {Navigate, useParams} from 'react-router-dom';

import {AdvertisingPlatformCodeEnum} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {SettingsTemplate, SettingsTemplateHeader} from '@dms/settings/shared';
import {formatDocumentTitle, isAdvertisementPlatformEnabled, usePermissions} from '@dms/shared';

import {getSafely, useComposePath} from 'shared/web';

import {AdvertisingPlatformBranchChoice} from '../AdvertisingPlatforms/components/AdvertisingPlatformBranchChoice';
import {useAdvertisingPlatformBranchQuerySync} from '../AdvertisingPlatforms/hooks/useAdvertisingPlatformBranchQuerySync';
import {PlatformAiDescriptionControl} from './components/PlatformAiDescriptionControl';
import {PlatformSettingsAudiPl} from './components/PlatformSettingsAudiPl';
import {PlatformSettingsAutocaris} from './components/PlatformSettingsAutocaris';
import {PlatformSettingsAutoplacPl} from './components/PlatformSettingsAutoplacPl';
import {PlatformSettingsDasWeltAuto} from './components/PlatformSettingsDasWeltAuto';
import {PlatformSettingsFacebook} from './components/PlatformSettingsFacebook';
import {PlatformSettingsFindCarPl} from './components/PlatformSettingsFindCarPl';
import {PlatformSettingsFordGeronimo} from './components/PlatformSettingsFordGeronimo';
import {PlatformSettingsFordUzywane} from './components/PlatformSettingsFordUzywane';
import {PlatformSettingsHyundaiPromise} from './components/PlatformSettingsHyundaiPromise';
import {PlatformSettingsMamGo} from './components/PlatformSettingsMamGo';
import {PlatformSettingsMercedesNaSklade} from './components/PlatformSettingsMercedesNaSklade';
import {PlatformSettingsMercedesNaSkladeRest} from './components/PlatformSettingsMercedesNaSkladeRest';
import {PlatformSettingsMobileDe} from './components/PlatformSettingsMobileDe';
import {PlatformSettingsMyWeb} from './components/PlatformSettingsMyWeb';
import {PlatformSettingsOlx} from './components/PlatformSettingsOlx';
import {PlatformSettingsOtomoto} from './components/PlatformSettingsOtomoto';
import {
  isPromptBridgeAdvertisingPlatform,
  PlatformSettingsPromptBridge,
} from './components/PlatformSettingsPromptBridge';
import {PlatformSettingsSamochodyPl} from './components/PlatformSettingsSamochodyPl';
import {PlatformSettingsSauto} from './components/PlatformSettingsSauto';
import {PlatformSettingsSkodaPlus} from './components/PlatformSettingsSkodaPlus';
import {PlatformSettingsTipCars} from './components/PlatformSettingsTipCars';
import {PlatformSettingsVehis} from './components/PlatformSettingsVehis';
import {PlatformSettingsVgp} from './components/PlatformSettingsVgp';
import {advertisingPlatformNames} from './utils/advertisingPlatformNames';

export function AdvertisingPlatformDetail() {
  const {code} = useParams<{code: AdvertisingPlatformCodeEnum}>();
  const composeRouterPath = useComposePath();
  useAdvertisingPlatformBranchQuerySync();
  const [canConfigureDescriptionGenerator] = usePermissions({
    permissionKeys: ['configureDescriptionGenerator'],
  });

  const platformListPath = composeRouterPath(settingsRoutes.advertisingPlatforms, {});

  if (code && !isAdvertisementPlatformEnabled({code})) {
    return <Navigate to={platformListPath} replace />;
  }

  const platformName =
    code && code in advertisingPlatformNames
      ? `${i18n.t('page.salesSettings.labels.platform')} ${getSafely(advertisingPlatformNames, code)}`
      : i18n.t('page.salesSettings.labels.platform');

  const HEADER: SettingsTemplateHeader = {
    title: platformName,
    breadcrumbs: [
      {
        label: i18n.t('page.salesSettings.labels.advertising'),
        href: settingsRoutes.advertising,
      },
      {
        label: i18n.t('page.settings.labels.advertisementPlatform'),
        href: platformListPath,
      },
    ],
    actions:
      code === AdvertisingPlatformCodeEnum.FASTBACK_B2B
        ? []
        : [
            {
              type: 'custom',
              component: <AdvertisingPlatformBranchChoice showLabel />,
            },
          ],
  };

  return (
    <>
      <Helmet
        title={formatDocumentTitle(
          platformName,
          i18n.t('page.settings.labels.advertisementPlatform'),
          i18n.t('page.salesSettings.labels.advertising'),
          i18n.t('page.settings.labels.title')
        )}
      />
      <SettingsTemplate header={HEADER} data-testid="settings-advertising-platform-detail">
        <VStack spacing={4}>
          {match(code)
            .with(AdvertisingPlatformCodeEnum.SAUTO, () => <PlatformSettingsSauto />)
            .with(AdvertisingPlatformCodeEnum.TIPCARS, () => <PlatformSettingsTipCars />)
            .with(AdvertisingPlatformCodeEnum.MOBILE_DE, () => <PlatformSettingsMobileDe />)
            .with(AdvertisingPlatformCodeEnum.MYWEB, () => <PlatformSettingsMyWeb />)
            .with(AdvertisingPlatformCodeEnum.FACEBOOK, () => <PlatformSettingsFacebook />)
            .with(AdvertisingPlatformCodeEnum.AUTOCARIS, () => <PlatformSettingsAutocaris />)
            .with(AdvertisingPlatformCodeEnum.OTOMOTO, () => <PlatformSettingsOtomoto />)
            .with(AdvertisingPlatformCodeEnum.OLX, () => <PlatformSettingsOlx />)
            .with(AdvertisingPlatformCodeEnum.SKODA_PLUS, () => <PlatformSettingsSkodaPlus />)
            .with(AdvertisingPlatformCodeEnum.DAS_WELT_AUTO, () => <PlatformSettingsDasWeltAuto />)
            .with(AdvertisingPlatformCodeEnum.MERCEDES_NA_SKLADE, () => (
              <PlatformSettingsMercedesNaSklade />
            ))
            .with(AdvertisingPlatformCodeEnum.MERCEDES_NA_SKLADE_REST, () => (
              <PlatformSettingsMercedesNaSkladeRest />
            ))
            .with(AdvertisingPlatformCodeEnum.HYUNDAI_PROMISE, () => (
              <PlatformSettingsHyundaiPromise />
            ))
            .with(AdvertisingPlatformCodeEnum.FORD_UZYWANE, () => <PlatformSettingsFordUzywane />)
            .with(AdvertisingPlatformCodeEnum.AUTOPLAC_PL, () => <PlatformSettingsAutoplacPl />)
            .with(AdvertisingPlatformCodeEnum.VEHIS, () => <PlatformSettingsVehis />)
            .with(AdvertisingPlatformCodeEnum.MAMGO, () => <PlatformSettingsMamGo />)
            .with(AdvertisingPlatformCodeEnum.FORD_GERONIMO, () => <PlatformSettingsFordGeronimo />)
            .with(AdvertisingPlatformCodeEnum.SAMOCHODY_PL, () => <PlatformSettingsSamochodyPl />)
            .with(AdvertisingPlatformCodeEnum.VGP, () => <PlatformSettingsVgp />)
            .with(AdvertisingPlatformCodeEnum.AUDI_PL, () => <PlatformSettingsAudiPl />)
            .with(AdvertisingPlatformCodeEnum.FINDCAR_PL, () => <PlatformSettingsFindCarPl />)
            .otherwise(() =>
              // eslint-disable-next-line eag/prefer-show -- Pre-existing violation
              isPromptBridgeAdvertisingPlatform(code) ? (
                <PlatformSettingsPromptBridge code={code} />
              ) : null
            )}
          <Show
            when={
              code && code in advertisingPlatformNames && canConfigureDescriptionGenerator
                ? code
                : null
            }
          >
            {(platformCode) => (
              <PlatformAiDescriptionControl
                platformCode={platformCode}
                data-testid="settings-advertising-platform-ai-description"
              />
            )}
          </Show>
        </VStack>
      </SettingsTemplate>
    </>
  );
}
